import { create } from 'zustand';

// Main application store
export const useAppStore = create((set, get) => ({
  // Connection status
  mt5Connected: false,
  setMt5Connected: (connected) => set({ mt5Connected: connected }),

  // Selected pair
  selectedPair: 'EURUSD',
  setSelectedPair: (pair) => set({ selectedPair: pair }),

  // Active tab
  activeTab: 'scanner',
  setActiveTab: (tab) => set({ activeTab: tab }),

  // Prices data
  prices: [],
  setPrices: (prices) => set({ prices }),
  updatePrice: (symbol, priceData) => set((state) => ({
    prices: state.prices.map((p) =>
      p.symbol === symbol ? { ...p, ...priceData } : p
    ),
  })),

  // Account info
  accountInfo: null,
  setAccountInfo: (info) => set({ accountInfo: info }),

  // Structure analysis
  structureAnalysis: {},
  setStructureAnalysis: (symbol, analysis) => set((state) => ({
    structureAnalysis: { ...state.structureAnalysis, [symbol]: analysis },
  })),

  // Confluence data
  confluenceData: {},
  setConfluenceData: (symbol, data) => set((state) => ({
    confluenceData: { ...state.confluenceData, [symbol]: data },
  })),

  // AI Checklist
  aiChecklist: null,
  setAiChecklist: (checklist) => set({ aiChecklist: checklist }),

  // AI Analysis
  aiAnalysis: null,
  setAiAnalysis: (analysis) => set({ aiAnalysis: analysis }),
  
  // Preset from leaderboard (for coherence with AI Analysis)
  leaderboardPreset: null,
  setLeaderboardPreset: (preset) => set({ leaderboardPreset: preset }),
  clearLeaderboardPreset: () => set({ leaderboardPreset: null }),
  
  // Active strategy/preset selected by user
  activeStrategy: null,
  setActiveStrategy: (strategy) => set({ activeStrategy: strategy ? String(strategy).toUpperCase().trim() : null }),

  // Journal stats
  journalStats: null,
  setJournalStats: (stats) => set({ journalStats: stats }),

  // Calendar data
  calendarData: [],
  setCalendarData: (data) => set({ calendarData: data }),

  // Weekly summary
  weeklySummary: [],
  setWeeklySummary: (data) => set({ weeklySummary: data }),

  // Loading states
  loading: {
    analysis: false,
    checklist: false,
    journal: false,
    prices: false,
  },
  setLoading: (key, value) => set((state) => ({
    loading: { ...state.loading, [key]: value },
  })),

  // Errors
  errors: {},
  setError: (key, error) => set((state) => ({
    errors: { ...state.errors, [key]: error },
  })),
  clearError: (key) => set((state) => {
    const { [key]: _, ...rest } = state.errors;
    return { errors: rest };
  }),

  // Sidebar state (mobile)
  sidebarOpen: false,
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
}));

// WebSocket store with exponential backoff
export const useWebSocketStore = create((set, get) => ({
  socket: null,
  connected: false,
  reconnectAttempts: 0,
  maxReconnectAttempts: 5,
  reconnectTimeout: null,

  connect: () => {
    const { reconnectAttempts, maxReconnectAttempts, reconnectTimeout } = get();
    
    // Clear any pending reconnect
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      set({ reconnectTimeout: null });
    }
    
    // Don't reconnect if we've exceeded max attempts
    if (reconnectAttempts >= maxReconnectAttempts) {
      console.log('WebSocket: Max reconnect attempts reached, stopping');
      return;
    }
    
    // Determine WebSocket URL
    // Strategia: usa sempre window.location per WebSocket (stesso dominio del frontend)
    // Questo evita problemi di CORS e configurazione
    const currentProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const currentHost = window.location.hostname;
    let wsUrl;
    
    // Se siamo in development (localhost), usa porta 8000
    if (currentHost === 'localhost' || currentHost === '127.0.0.1') {
      wsUrl = `${currentProtocol}//${currentHost}:8000/api/ws`;
      console.log('🔌 Development mode - using localhost:8000');
    } else {
      // Produzione: usa stesso dominio del frontend
      // Rimuovi porta se presente (WebSocket non usa porta standard)
      const hostWithoutPort = currentHost.split(':')[0];
      wsUrl = `${currentProtocol}//${hostWithoutPort}/api/ws`;
      console.log('🔌 Production mode - using same domain as frontend');
    }

    console.log('🔌 WebSocket: Attempting connection (attempt', reconnectAttempts + 1, ')');
    console.log('🔌 WebSocket URL:', wsUrl);
    console.log('🔌 Current location:', window.location.href);
    
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      set({ socket: ws, connected: true, reconnectAttempts: 0 });
      console.log('✅ WebSocket connected to:', wsUrl);
      console.log('✅ WebSocket connection established - notifications will be received');
      console.log('📱 Device type:', /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'Mobile' : 'Desktop');
      
      // Opzionale: Invia user_id al backend se disponibile (per filtrare notifiche)
      // const userId = useUser()?.user?.id;
      // if (userId) {
      //   ws.send(JSON.stringify({ type: 'auth', user_id: userId }));
      // }
    };

    ws.onclose = (event) => {
      const currentAttempts = get().reconnectAttempts;
      set({ socket: null, connected: false });
      console.log('❌ WebSocket disconnected');
      console.log('❌ Close code:', event.code);
      console.log('❌ Close reason:', event.reason || 'No reason provided');
      console.log('❌ Was clean:', event.wasClean);
      
      // Se è una chiusura pulita (code 1000) o errore di configurazione (1006), non riconnettere
      if (event.code === 1000 || (event.code === 1006 && currentAttempts >= 3)) {
        console.log('⚠️ WebSocket: Stopping reconnection attempts (clean close or persistent error)');
        return;
      }
      
      // Exponential backoff: 3s, 6s, 12s, 24s, 48s
      const delay = Math.min(3000 * Math.pow(2, currentAttempts), 60000);
      console.log(`🔄 WebSocket: Will reconnect in ${delay/1000}s (attempt ${currentAttempts + 1})`);
      
      const timeout = setTimeout(() => {
        set({ reconnectAttempts: currentAttempts + 1, reconnectTimeout: null });
        get().connect();
      }, delay);
      
      set({ reconnectTimeout: timeout });
    };

    ws.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
      console.error('❌ Error details:', {
        type: error.type,
        target: error.target,
        url: wsUrl
      });
      // Don't trigger additional reconnect here - onclose will handle it
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log('📨 WebSocket message received:', message.type, message);

        if (message.type === 'price_update') {
          useAppStore.getState().setPrices(message.data);
        }

        if (message.type === 'analysis_update') {
          useAppStore.getState().setStructureAnalysis(message.symbol, message.analysis);
          useAppStore.getState().setConfluenceData(message.symbol, message.confluence);
        }

        // Handle score_alert notifications
        if (message.type === 'score_alert') {
          const isMobile = /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent);
          console.log('🔔 Score alert received:', message);
          console.log('📱 Device type:', isMobile ? 'Mobile' : 'Desktop');
          
          // Estrai i dati della notifica
          const data = message.data || message;
          
          // Filtra notifiche non valide:
          // 1. Score increase deve essere > 0 (non accettiamo +0% o negativi)
          // 2. Score increase deve essere significativo (almeno 1%)
          // 3. Current score deve essere >= 65 (soglia minima)
          
          // Converti score_increase a numero (gestisce stringhe, null, undefined)
          const scoreIncrease = parseFloat(data.score_increase) || 0;
          const currentScore = parseFloat(data.current_score) || 0;
          
          // Log per debug
          console.log('🔍 Validating notification:', {
            score_increase: scoreIncrease,
            current_score: currentScore,
            raw_data: data,
            device: isMobile ? 'Mobile' : 'Desktop'
          });
          
          // Controllo rigoroso: score_increase deve essere un numero positivo >= 1
          if (isNaN(scoreIncrease) || scoreIncrease <= 0) {
            console.log('⚠️ Notification rejected: score_increase <= 0 or NaN', scoreIncrease);
            return;
          }
          
          if (scoreIncrease < 1) {
            console.log('⚠️ Notification rejected: score_increase < 1%', scoreIncrease);
            return;
          }
          
          if (isNaN(currentScore) || currentScore < 65) {
            console.log('⚠️ Notification rejected: current_score < 65 or NaN', currentScore);
            return;
          }
          
          // Se passa tutti i filtri, aggiungi la notifica
          useNotificationStore.getState().addNotification(message);
          const storeState = useNotificationStore.getState();
          console.log('✅ Notification added to store');
          console.log('📱 Device type:', isMobile ? 'Mobile' : 'Desktop');
          console.log('📊 Total notifications in store:', storeState.notifications.length);
          console.log('📊 Unread notifications:', storeState.unreadCount);
          console.log('📱 Mobile check - notifications visible:', isMobile ? 'Check MobileSidebar' : 'Desktop');
        }
      } catch (error) {
        console.error('❌ Error parsing WebSocket message:', error, event.data);
      }
    };

    set({ socket: ws });
  },

  disconnect: () => {
    const { socket, reconnectTimeout } = get();
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
    }
    if (socket) {
      socket.close();
    }
    set({ socket: null, connected: false, reconnectAttempts: 0, reconnectTimeout: null });
  },
  
  // Reset reconnect attempts (call this when user manually reconnects)
  resetReconnect: () => {
    set({ reconnectAttempts: 0 });
  },

  send: (message) => {
    const { socket, connected } = get();
    if (socket && connected) {
      socket.send(JSON.stringify(message));
    }
  },
}));

// Notification store with persistence
export const useNotificationStore = create((set, get) => {
  // Load from localStorage on init
  const loadFromStorage = () => {
    try {
      const stored = localStorage.getItem('notifications-storage');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.state?.notifications || [];
      }
    } catch (e) {
      console.error('Error loading notifications from storage:', e);
    }
    return [];
  };

  const loadNotifications = () => {
    const notifications = loadFromStorage();
    return {
      notifications,
      unreadCount: notifications.filter(n => !n.read).length,
    };
  };

  const initialState = loadNotifications();

  return {
    ...initialState,

    addNotification: (notification) => {
      // Il backend invia: { type: 'score_alert', data: {...} }
      // Dobbiamo estrarre i dati correttamente
      const notificationData = notification.data || notification;
      
      // Validazione aggiuntiva nel store (doppio controllo)
      const scoreIncrease = parseFloat(notificationData.score_increase) || 0;
      const currentScore = parseFloat(notificationData.current_score) || 0;
      
      // Controllo rigoroso
      if (isNaN(scoreIncrease) || scoreIncrease <= 0 || scoreIncrease < 1) {
        console.log('⚠️ Notification rejected in store: invalid score_increase', { scoreIncrease, raw: notificationData.score_increase });
        return;
      }
      
      if (isNaN(currentScore) || currentScore < 65) {
        console.log('⚠️ Notification rejected in store: invalid current_score', { currentScore, raw: notificationData.current_score });
        return;
      }
      
      const stored = {
        ...notificationData,
        id: notification.id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: notification.timestamp || new Date().toISOString(),
        read: false,
        type: notification.type || 'score_alert',
      };
      
      console.log('📝 Adding notification:', stored);
      
      set((state) => {
        // Evita duplicati (stesso symbol + direction + score) entro 10 secondi
        const isDuplicate = state.notifications.some(n => {
          const nData = n.data || n;
          const storedData = stored.data || stored;
          return (
            (nData.symbol || n.symbol) === (storedData.symbol || stored.symbol) && 
            (nData.direction || n.direction) === (storedData.direction || stored.direction) && 
            (nData.current_score || n.current_score) === (storedData.current_score || stored.current_score) &&
            Math.abs(new Date(n.timestamp || nData.timestamp) - new Date(stored.timestamp)) < 10000 // 10 secondi
          );
        });
        
        if (isDuplicate) {
          console.log('⚠️ Duplicate notification ignored');
          return state;
        }
        
        const updated = [stored, ...state.notifications].slice(0, 100); // Max 100 notifiche
        const unreadCount = updated.filter(n => !n.read).length;
        // Save to localStorage
        try {
          localStorage.setItem('notifications-storage', JSON.stringify({ state: { notifications: updated } }));
        } catch (e) {
          console.error('Error saving notifications to storage:', e);
        }
        console.log('✅ Notification stored, total:', updated.length, 'unread:', unreadCount);
        return { notifications: updated, unreadCount };
      });
    },

    markAsRead: (id) => {
      set((state) => {
        const updated = state.notifications.map((n) =>
          n.id === id ? { ...n, read: true, readAt: new Date().toISOString() } : n
        );
        const unreadCount = updated.filter(n => !n.read).length;
        // Save to localStorage
        try {
          localStorage.setItem('notifications-storage', JSON.stringify({ state: { notifications: updated } }));
        } catch (e) {
          console.error('Error saving notifications to storage:', e);
        }
        console.log('✅ Notification marked as read, unread count:', unreadCount);
        return { notifications: updated, unreadCount };
      });
    },

    markAllAsRead: () => {
      set((state) => {
        const updated = state.notifications.map((n) => ({
          ...n,
          read: true,
          readAt: n.readAt || new Date().toISOString(),
        }));
        const unreadCount = 0; // Tutte lette
        // Save to localStorage
        try {
          localStorage.setItem('notifications-storage', JSON.stringify({ state: { notifications: updated } }));
        } catch (e) {
          console.error('Error saving notifications to storage:', e);
        }
        console.log('✅ All notifications marked as read, unread count:', unreadCount);
        return { notifications: updated, unreadCount };
      });
    },

    deleteNotification: (id) => {
      set((state) => {
        const updated = state.notifications.filter((n) => n.id !== id);
        const unreadCount = updated.filter(n => !n.read).length;
        // Save to localStorage
        try {
          localStorage.setItem('notifications-storage', JSON.stringify({ state: { notifications: updated } }));
        } catch (e) {
          console.error('Error saving notifications to storage:', e);
        }
        return { notifications: updated, unreadCount };
      });
    },

    clearAll: () => {
      set({ notifications: [], unreadCount: 0 });
      try {
        localStorage.removeItem('notifications-storage');
      } catch (e) {
        console.error('Error clearing notifications from storage:', e);
      }
    },
  };
});
